import React from "react";
import { toast } from "react-toastify";

import { KintoneExportSetting } from "@graffer-inc/cloud-gov-core";
import {
  gqlClient,
  gqlHeader,
  reportUnexpectedError,
  runGqlQueryV2,
} from "@graffer-inc/smart-apply";

import {
  DefaultNumItemsPerPages,
  toTotalPage,
} from "../../../../../../application/domain/pagination/paging";
import { useHistory } from "react-router-dom";

const settingsNumPerPage = DefaultNumItemsPerPages;

type Props<TENANT_ID extends string | undefined> = {
  tenantId: TENANT_ID;
  groupIds?: string[];
  detailLinkProvider: (arg: {
    tenantId: TENANT_ID;
    kintoneExportSettingId: string;
  }) => string;
  createLinkProvider: (arg: { tenantId: TENANT_ID }) => string;
};

export const useKintoneExportSettings = <TENANT_ID extends string | undefined>({
  tenantId,
  groupIds,
  detailLinkProvider,
  createLinkProvider,
}: Props<TENANT_ID>) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [kintoneExportSettings, setKintoneExportSettings] = React.useState<
    Pick<KintoneExportSetting, "id" | "name" | "formResourceId" | "disabled">[]
  >([]);
  const [page, setPage] = React.useState(1);
  const [lastPage, setLastPage] = React.useState(1);

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }

    const fetchKintoneExportSettings = async () => {
      setIsLoading(true);
      try {
        const res = await runGqlQueryV2(() => {
          return gqlClient.kintoneExportSettings(
            {
              tenantId,
              groupIds:
                !!groupIds && groupIds.length > 0 ? groupIds : undefined,
              page: {
                limit: settingsNumPerPage,
                offset: settingsNumPerPage * (page - 1),
              },
            },
            gqlHeader("q"),
          );
        });

        if (res.error) {
          reportUnexpectedError(res.error.rawError);
          toast.error("kintone 連携設定の取得に失敗しました。");
          return;
        }

        setKintoneExportSettings(
          res.data!.kintoneExportSettingsV2.edges.map(({ node }) => {
            return node;
          }),
        );
        setLastPage(
          toTotalPage(
            res.data!.kintoneExportSettingsV2.totalCount,
            settingsNumPerPage,
          ),
        );
      } catch (err) {
        reportUnexpectedError(err);
        toast.error("kintone 連携設定の取得に失敗しました。");
      } finally {
        setIsLoading(false);
      }
    };
    fetchKintoneExportSettings();
  }, [
    tenantId,
    groupIds,
    setIsLoading,
    setKintoneExportSettings,
    page,
    setLastPage,
  ]);

  const history = useHistory();
  const handleKintoneExportSettingRowClick = React.useCallback(
    (id: string) => {
      history.push(
        detailLinkProvider({
          tenantId,
          kintoneExportSettingId: id,
        }),
      );
    },
    [history, tenantId, detailLinkProvider],
  );
  const handleCreateKintoneExportSetting = React.useCallback(() => {
    history.push(createLinkProvider({ tenantId }));
  }, [history, tenantId, createLinkProvider]);

  return {
    isLoading,
    kintoneExportSettings,
    page,
    setPage,
    lastPage,
    handleKintoneExportSettingRowClick,
    handleCreateKintoneExportSetting,
  };
};
